import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography
} from '@mui/material';
import { supabase } from '../../supabase-config';

/**
 * TechMultiSelect - Shared multi-technician assignment component
 *
 * Replaces single-select tech dropdowns with multi-select Autocomplete.
 * Syncs with the `job_assignments` junction table and maintains backward
 * compatibility by setting `jobs.assigned_tech_id` to the first selected tech.
 *
 * @param {Array} technicians - Available technicians [{id, full_name}]
 * @param {string} jobId - The job ID to load/save assignments for
 * @param {string} initialTechId - Legacy single assigned_tech_id (fallback if no job_assignments exist)
 * @param {function} onChange - Callback with selected tech IDs array: (techIds) => void
 * @param {string} [size='small'] - MUI size prop
 * @param {string} [label='Assigned Technicians'] - Field label
 * @param {boolean} [disabled=false] - Disable the field
 */
const TechMultiSelect = ({
  technicians = [],
  jobId,
  initialTechId,
  onChange,
  size = 'small',
  label = 'Assigned Technicians',
  disabled = false
}) => {
  const [selectedTechs, setSelectedTechs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Load existing assignments from job_assignments table
  useEffect(() => {
    if (!jobId) {
      // No job yet — use initialTechId as fallback
      if (initialTechId && technicians.length > 0) {
        const tech = technicians.find(t => t.id === initialTechId);
        if (tech) {
          setSelectedTechs([tech]);
        }
      }
      setLoaded(true);
      return;
    }

    const loadAssignments = async () => {
      try {
        const { data, error } = await supabase
          .from('job_assignments')
          .select('technician_id')
          .eq('job_id', jobId);

        if (error) throw error;

        if (data && data.length > 0) {
          // Match assignment IDs to technician objects
          const assignedTechs = data
            .map(a => technicians.find(t => t.id === a.technician_id))
            .filter(Boolean);
          setSelectedTechs(assignedTechs);
        } else if (initialTechId) {
          // Fallback to legacy single tech
          const tech = technicians.find(t => t.id === initialTechId);
          if (tech) {
            setSelectedTechs([tech]);
          }
        }
      } catch (err) {
        console.error('Failed to load tech assignments:', err);
        // Fallback to legacy single tech on error
        if (initialTechId) {
          const tech = technicians.find(t => t.id === initialTechId);
          if (tech) {
            setSelectedTechs([tech]);
          }
        }
      } finally {
        setLoaded(true);
      }
    };

    if (technicians.length > 0) {
      loadAssignments();
    }
  }, [jobId, initialTechId, technicians]);

  const handleChange = useCallback((_, newValue) => {
    setSelectedTechs(newValue);
    if (onChange) {
      onChange(newValue.map(t => t.id));
    }
  }, [onChange]);

  if (!loaded) return null;

  return (
    <Autocomplete
      multiple
      options={technicians}
      getOptionLabel={(tech) => tech.full_name || ''}
      value={selectedTechs}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField {...params} label={label} />
      )}
      renderTags={(value, getTagProps) =>
        value.map((tech, index) => (
          <Chip
            label={tech.full_name}
            size="small"
            color="primary"
            variant="outlined"
            {...getTagProps({ index })}
            key={tech.id}
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Typography variant="body2">{option.full_name}</Typography>
        </Box>
      )}
      noOptionsText="No technicians available"
    />
  );
};

/**
 * Save multi-tech assignments for a job.
 * Syncs the `job_assignments` table and updates the legacy `assigned_tech_id` FK.
 *
 * @param {string} jobId - The job UUID
 * @param {string[]} techIds - Array of technician UUIDs to assign
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const saveTechAssignments = async (jobId, techIds) => {
  try {
    // Delete existing assignments
    const { error: deleteError } = await supabase
      .from('job_assignments')
      .delete()
      .eq('job_id', jobId);

    if (deleteError) throw deleteError;

    // Insert new assignments with technician names
    if (techIds.length > 0) {
      // Look up tech names for the denormalized column
      const { data: techUsers, error: techError } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', techIds);

      if (techError) throw techError;

      const techNameMap = {};
      (techUsers || []).forEach(u => { techNameMap[u.id] = u.full_name; });

      const { error: insertError } = await supabase
        .from('job_assignments')
        .insert(techIds.map(techId => ({
          job_id: jobId,
          technician_id: techId,
          technician_name: techNameMap[techId] || null
        })));

      if (insertError) throw insertError;
    }

    // Update legacy FK for backward compatibility (first tech or null)
    const { error: updateError } = await supabase
      .from('jobs')
      .update({ assigned_tech_id: techIds[0] || null })
      .eq('id', jobId);

    if (updateError) throw updateError;

    return { success: true };
  } catch (err) {
    console.error('Failed to save tech assignments:', err);
    return { success: false, error: err.message };
  }
};

export default TechMultiSelect;
